import { useConnection } from 'hooks/useConnection'
import { useCallback, useMemo } from 'react'
import { IslandApiResponse } from 'state/data/slice'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { MemoryFilter } from 'state/kodiakIslands/types'

import { activateFilter, deactivateFilter, setSearchTerm, updateKodiakIslands } from './reducer'

/**
 * Returns the total TVL across all islands
 */
export function useKodiakIslandTotalTvl(): number {
  const kodiakIslands = useAppSelector((state) => state.kodiakIslands.kodiakIslands)
  return useMemo(() => {
    if (!kodiakIslands || kodiakIslands.length === 0) return 0
    return kodiakIslands.reduce((acc, island) => acc + Number(island.tvl) || 0, 0)
  }, [kodiakIslands])
}

/**
 * Returns all islands from the Redux store
 */
export function useKodiakIslands(): IslandApiResponse[] {
  const kodiakIslands = useAppSelector((state) => state.kodiakIslands.kodiakIslands)
  return useMemo(() => kodiakIslands, [kodiakIslands])
}

/**
 * Hook to update the Kodiak islands in the Redux store
 */
export function useUpdateKodiakIslands(): (kodiakIslands: IslandApiResponse[] | undefined) => void {
  const dispatch = useAppDispatch()
  const { chainId } = useConnection()
  return useCallback(
    (kodiakIslands: IslandApiResponse[] | undefined) => {
      if (!kodiakIslands || kodiakIslands.length === 0 || !chainId) return
      dispatch(
        updateKodiakIslands({
          kodiakIslands,
        })
      )
    },
    [dispatch, chainId]
  )
}

/**
 * Checks if a specific filter is active
 */
export function useIsFilterActive(filter: MemoryFilter): boolean {
  const filters = useAppSelector((state) => state.kodiakIslands.filters)
  return useMemo(() => filters.find((f) => f.displayName === filter.displayName)?.isActive || false, [filters, filter])
}

/**
 * Activates a specific filter
 */
export function useActivateFilter(filter: MemoryFilter) {
  const dispatch = useAppDispatch()
  return useCallback(() => dispatch(activateFilter(filter)), [dispatch, filter])
}

/**
 * Deactivates a specific filter
 */
export function useDeactivateFilter(filter: MemoryFilter) {
  const dispatch = useAppDispatch()
  return useCallback(() => dispatch(deactivateFilter(filter)), [dispatch, filter])
}

/**
 * Sets the search term
 */
export function useSetSearchTerm() {
  const dispatch = useAppDispatch()
  return useCallback((searchTerm: string) => dispatch(setSearchTerm(searchTerm)), [dispatch])
}

/**
 * Gets the current search term
 */
export function useSearchTerm(): string {
  return useAppSelector((state) => state.kodiakIslands.searchTerm)
}
